// cpq2-owned hide rules for WHOLE QUESTIONS.
//
// The sibling of answerHideRules, one level up. That one narrows the options inside a
// question; this one takes the question off the form entirely, under a condition cpq2 owns
// rather than one the NetSuite export happens to carry.
//
// Kevin, 2026-09-21, on NDB: "if the design is anything actuator (AC, acrn, acln, easac,
// easacrn, easacln) then you will never have hood spacers, you will never have a face plate
// so none." Asking for either on an actuator design is asking a question with one answer.
//
// WHY IT WRITES custrecord_cpqc_question_p_hide_rule, same reasoning as answerHideRules: that
// field is the one thing the whole app already agrees on -- the form skips the question,
// validation stops requiring it, the mapping's hidden-field handling sees it hidden. Merging
// into it means a rule cpq2 owns behaves exactly like one NetSuite authored, everywhere,
// rather than in the two places someone remembered to wire up.
//
// WHY NOT EDIT questions.json: scripts/extract-product.cjs rewrites that folder wholesale on
// the next import and the edit would vanish with nothing failing.
//
// AND WHY HIDING IS NEVER THE WHOLE RULE. A hidden question KEEPS ITS ANSWER (see §8.3 of the
// build spec). If the field reaches the item, the BOM or the key, a stale value still gets
// there -- so a hide rule that matters is always paired with an operation that forces the
// value. Hiding stops it being picked; the operation stops it surviving.
import { getProfile } from "../products";
import type { Question } from "../types";

/** OR two parsed-rule expressions. Either saying "hide" hides, so a cpq2 rule can only ever
 *  hide MORE than the export already does -- it can never reveal a question NetSuite hides. */
function orRules(existing: string, added: string): string {
  const a = (existing ?? "").trim();
  if (!a || a === "false") return added;
  if (a === "true") return "true"; // already always hidden; nothing to add
  return `(${a})||(${added})`;
}

/**
 * Apply a product's questionHideRules to its questions, IN PLACE.
 *
 * Returns how many questions were given a rule — what a caller logs and a test asserts on. A
 * rule naming a question that does not exist would otherwise do nothing, silently, and look
 * exactly like a rule that was applied.
 */
/**
 * Apply a product's questionLockRules, IN PLACE: OR'd into the question's read-only rule, which
 * Question.tsx already evaluates (a locked question stays on the form, shows its value and
 * encodes it). Returns how many questions were given a rule.
 */
export function applyQuestionLockRules(product: string, questions: Question[]): number {
  const rules = getProfile(product).questionLockRules ?? [];
  let touched = 0;
  for (const rule of rules) {
    for (const code of rule.questions) {
      const q = questions.find((x) => x.custrecord_cpqc_question_code === code);
      if (!q) continue;
      q.custrecord_cpqc_question_parsed_ro_rule = orRules(String(q.custrecord_cpqc_question_parsed_ro_rule ?? ""), rule.lockedWhen);
      touched++;
    }
  }
  return touched;
}

export function applyQuestionHideRules(product: string, questions: Question[]): number {
  const rules = getProfile(product).questionHideRules ?? [];
  if (rules.length === 0) return 0;
  let touched = 0;
  for (const rule of rules) {
    for (const code of rule.questions) {
      const q = questions.find((x) => x.custrecord_cpqc_question_code === code);
      if (!q) continue;
      q.custrecord_cpqc_question_p_hide_rule = orRules(
        q.custrecord_cpqc_question_p_hide_rule ?? "",
        rule.hiddenWhen
      );
      touched++;
    }
  }
  return touched;
}
